import { useAuth } from "@/context/AuthContext";
import { useEffect, useState, useRef } from "react";
import Topbar from "@/components/Topbar";
import LogoUploadPanel from "@/components/LogoUploadPanel";
import CompactTransactionsStrip from "@/components/CompactTransactionsStrip";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { FilePdf, UploadSimple, Trash } from "@phosphor-icons/react";

export default function Settings() {
  const { user, refresh } = useAuth();
  const [currency, setCurrency] = useState("");
  const [savingCur, setSavingCur] = useState(false);
  const [jrRate, setJrRate] = useState("");
  const [srRate, setSrRate] = useState("");
  const [savingRates, setSavingRates] = useState(false);
  const [nudgesOn, setNudgesOn] = useState(true);
  const [savingNudges, setSavingNudges] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  useEffect(() => {
    setCurrency((user?.preferences?.preferred_currency || "").toUpperCase());
    setJrRate(user?.preferences?.jr_rate_usd ?? "");
    setSrRate(user?.preferences?.sr_rate_usd ?? "");
    // v3.2.17 · Milestone email nudges default ON (opt-out semantics).
    setNudgesOn(user?.preferences?.email_nudges_enabled !== false);
  }, [user?.preferences?.preferred_currency,
      user?.preferences?.jr_rate_usd,
      user?.preferences?.sr_rate_usd,
      user?.preferences?.email_nudges_enabled]);
  const saveCurrency = async () => {
    setSavingCur(true);
    try {
      await api.patch("/auth/me/preferences", { preferred_currency: currency || "" });
      toast.success(currency ? `Preferred currency set to ${currency}.` : "Preferred currency cleared.");
      if (refresh) await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSavingCur(false); }
  };
  const saveRates = async () => {
    setSavingRates(true);
    try {
      await api.patch("/auth/me/preferences", {
        jr_rate_usd: Number(jrRate) || 0,
        sr_rate_usd: Number(srRate) || 0,
      });
      toast.success("Billing rates saved.");
      if (refresh) await refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSavingRates(false); }
  };
  const toggleNudges = async (next) => {
    setSavingNudges(true);
    setNudgesOn(next);
    try {
      await api.patch("/auth/me/preferences", { email_nudges_enabled: next });
      toast.success(next
        ? "Milestone email nudges ON — we'll email ~48h before auto-release."
        : "Milestone email nudges OFF. In-app notifications still fire.");
      if (refresh) await refresh();
    } catch (e) {
      setNudgesOn(!next);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSavingNudges(false); }
  };
  const sendDigestPreview = async () => {
    setPreviewBusy(true);
    try {
      const { data } = await api.post("/saved-searches/digest-preview");
      if (data.sent) {
        toast.success(`Preview sent — ${data.match_count} match${data.match_count !== 1 ? "es" : ""} in your inbox.`);
      } else {
        toast.info(data.reason || "Nothing to preview yet.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Preview failed");
    } finally { setPreviewBusy(false); }
  };
  if (!user) return null;
  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar active="settings" />
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        <div className="label-tag mb-2">// Account</div>
        <h1 className="font-display text-4xl font-black tracking-tighter">Settings</h1>

        <div className="mt-8 border border-white/10">
          <div className="border-b border-white/10 p-4 bg-[#1A1F29]">
            <div className="label-tag">Profile</div>
          </div>
          <div className="p-4 font-mono text-sm space-y-3">
            <div className="grid grid-cols-3 gap-4 py-2 border-b border-white/5">
              <div className="text-gray-500">Name</div>
              <div className="col-span-2 text-white" data-testid="settings-name">{user.name || "—"}</div>
            </div>
            <div className="grid grid-cols-3 gap-4 py-2 border-b border-white/5">
              <div className="text-gray-500">Email</div>
              <div className="col-span-2 text-white" data-testid="settings-email">{user.email}</div>
            </div>
            <div className="grid grid-cols-3 gap-4 py-2">
              <div className="text-gray-500">Role</div>
              <div className="col-span-2 text-[#00E5FF] uppercase" data-testid="settings-role">{user.role}</div>
            </div>
          </div>
        </div>

        {/* v3.2.35 · Compact recent transactions strip — settings mini-widget. */}
        <CompactTransactionsStrip role={user.role} />

        {/* v3.2.20 · PE license-expired banner (soft suspend). */}
        {user.role === "pe" && user.pe_status === "license_expired" && (
          <div className="mt-4 border border-[#FF8A85]/60 bg-[#FF8A85]/5 p-4"
               data-testid="pe-license-expired-banner">
            <div className="flex items-start gap-3">
              <div className="text-[10px] font-mono text-[#FF8A85] uppercase tracking-wider shrink-0 mt-0.5">
                // license expired
              </div>
              <div className="text-sm">
                <div className="text-white font-semibold">Your engineering license has expired.</div>
                <div className="text-gray-300 mt-1">
                  New proposals are blocked. Existing engagements continue.
                  Upload your renewed license below — an admin will verify it and reactivate your account.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* v3.2.22 · PE license renewal upload — PE-only. */}
        {user.role === "pe" && (<PELicenseFileCard />)}

        {/* v3.2.27 · CAD Template wizard, library, marketplace, and
            per-project title block editor were removed from Settings
            per product feedback. Logo upload stays. */}
        <div className="mt-6 space-y-6">
          <LogoUploadPanel />
        </div>

        <div className="mt-6 border border-white/10">
          <div className="border-b border-white/10 p-4 bg-[#1A1F29]">
            <div className="label-tag">Workspace defaults</div>
          </div>
          <div className="p-4 text-sm text-gray-400 space-y-4">
            <div>
              Default code standards, voltage and units can be configured per-project in the builder.
              Workspace-wide preferences are coming soon.
            </div>
            {(user.role === "client" || user.role === "admin") && (
              <div className="border-t border-white/5 pt-4"
                   data-testid="settings-currency-block">
                <div className="text-white text-sm mb-1">Preferred display currency</div>
                <div className="text-xs text-gray-400 mb-3">
                  Engineers post proposals in USD. What you see across the app will be converted to
                  this currency. Leave blank to auto-detect from your job location (default: USD).
                </div>
                <div className="flex items-center gap-2">
                  <select value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    className="bg-black/40 border border-white/10 px-3 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
                    data-testid="settings-currency-select">
                    <option value="">Auto (from job location)</option>
                    {["USD","EUR","GBP","CAD","AUD","JPY","CHF","CNY","HKD","SGD","INR","MXN","BRL","ZAR","AED","NGN","KRW","SEK","NOK","DKK"].map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                  <button onClick={saveCurrency} disabled={savingCur}
                    className="btn-primary text-sm py-2 px-4 disabled:opacity-40"
                    data-testid="settings-currency-save">
                    {savingCur ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            )}
            {(user.role === "pe" || user.role === "admin") && (
              <div className="border-t border-white/5 pt-4"
                   data-testid="settings-rates-block">
                <div className="text-white text-sm mb-1">Billing rates (USD / hour)</div>
                <div className="text-xs text-gray-400 mb-3">
                  Used to auto-compute the <b className="text-[#00E5FF]">Cost</b> column when
                  you fill out the itemized scope of a proposal. Both rates can be zero — the
                  Cost column becomes editable in that case.
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <label className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">Jr / Int</label>
                  <input type="number" min={0} step={5} value={jrRate}
                    onChange={(e) => setJrRate(e.target.value)}
                    placeholder="e.g. 85"
                    className="w-24 bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
                    data-testid="settings-jr-rate" />
                  <label className="text-[10px] font-mono text-gray-500 uppercase tracking-wider ml-2">Sr</label>
                  <input type="number" min={0} step={5} value={srRate}
                    onChange={(e) => setSrRate(e.target.value)}
                    placeholder="e.g. 175"
                    className="w-24 bg-black/40 border border-white/10 px-2 py-2 text-sm font-mono focus:border-[#00E5FF] outline-none"
                    data-testid="settings-sr-rate" />
                  <button onClick={saveRates} disabled={savingRates}
                    className="btn-primary text-sm py-2 px-4 disabled:opacity-40"
                    data-testid="settings-rates-save">
                    {savingRates ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            )}
            {/* v3.2.17 · Milestone email nudges toggle — visible to PE + Client (both parties receive nudges). */}
            {(user.role === "pe" || user.role === "client" || user.role === "admin") && (
              <div className="border-t border-white/5 pt-4"
                   data-testid="settings-nudges-block">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="text-white text-sm mb-1">Milestone email nudges</div>
                    <div className="text-xs text-gray-400">
                      Get an email ~48h before a submitted milestone auto-releases,
                      so nothing slips through the 7-day timer. In-app notifications
                      always fire regardless.
                    </div>
                  </div>
                  <button
                    onClick={() => toggleNudges(!nudgesOn)}
                    disabled={savingNudges}
                    role="switch"
                    aria-checked={nudgesOn}
                    className={`shrink-0 w-14 h-7 rounded-full border transition-colors font-mono text-[10px] uppercase tracking-wider inline-flex items-center justify-center ${
                      nudgesOn
                        ? "bg-[#00E5FF]/20 border-[#00E5FF]/60 text-[#00E5FF]"
                        : "bg-white/5 border-white/15 text-gray-400"
                    } disabled:opacity-40`}
                    data-testid="settings-nudges-toggle">
                    {nudgesOn ? "ON" : "OFF"}
                  </button>
                </div>
                {/* v3.2.19 · Digest preview button — PE-only. Fires the Monday digest
                    immediately, bypassing day/cooldown gates, still respecting the toggle. */}
                {user.role === "pe" && (
                  <div className="mt-3 flex items-center gap-3">
                    <button
                      onClick={sendDigestPreview}
                      disabled={previewBusy || !nudgesOn}
                      title={!nudgesOn ? "Turn nudges ON to receive a preview" : ""}
                      className="text-xs font-mono px-3 py-2 border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="settings-digest-preview-btn">
                      {previewBusy ? "Sending…" : "Send me a preview →"}
                    </button>
                    <span className="text-[10px] text-gray-500 font-mono">
                      Emails the current weekly digest to yourself right now.
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// v3.2.22 · PE license renewal upload card.
function PELicenseFileCard() {
  const [info, setInfo] = useState(null);   // {present, filename, uploaded_at, content_type}
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pe/me/license-file");
      setInfo(data);
    } catch (e) {
      setInfo({ present: false });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/pe/me/license-file", fd,
                       { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("License file uploaded. Your admin will review and update your expiry.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = async () => {
    try {
      const resp = await api.get("/pe/me/license-file/download",
                                    { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 30 * 1000);
    } catch (e) {
      toast.error("Could not open the file");
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete your uploaded license file?")) return;
    setDeleting(true);
    try {
      await api.delete("/pe/me/license-file");
      toast.success("License file deleted.");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    } finally { setDeleting(false); }
  };

  return (
    <div className="mt-6 border border-white/10 bg-[#12151C] p-4" data-testid="pe-license-file-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-white text-sm font-semibold">License renewal file</div>
          <div className="text-xs text-gray-400 mt-1 max-w-lg">
            Upload your signed P.Eng. license (PDF, PNG, or JPEG · 10 MB max).
            Your admin will manually verify it and update your expiry date.
            Uploading a new file overwrites the previous one.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input ref={fileRef} type="file"
                 accept="application/pdf,image/png,image/jpeg"
                 onChange={onFile}
                 className="hidden"
                 data-testid="pe-license-file-input" />
          <button
            onClick={onPick}
            disabled={uploading}
            className="inline-flex items-center gap-2 text-xs font-mono px-3 py-2 border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 disabled:opacity-40"
            data-testid="pe-license-file-upload-btn">
            <UploadSimple size={14} weight="bold" />
            {uploading ? "Uploading…" : info?.present ? "Replace" : "Upload"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="mt-3 text-xs font-mono text-gray-500">loading…</div>
      ) : info?.present ? (
        <div className="mt-4 flex items-center gap-3 border-t border-white/5 pt-3 text-sm"
             data-testid="pe-license-file-present">
          <FilePdf size={20} className="text-[#00E5FF]" weight="fill" />
          <div className="flex-1 min-w-0">
            <div className="text-white truncate" data-testid="pe-license-file-name">
              {info.filename || "license"}
            </div>
            <div className="text-[10px] font-mono text-gray-500">
              uploaded {info.uploaded_at ? new Date(info.uploaded_at).toLocaleString() : "—"}
            </div>
          </div>
          <button
            onClick={download}
            className="text-[10px] font-mono px-2 py-1 border border-white/15 text-gray-300 hover:text-[#00E5FF] hover:border-[#00E5FF]/40"
            data-testid="pe-license-file-view-btn">
            view
          </button>
          <button
            onClick={remove}
            disabled={deleting}
            className="text-[10px] font-mono px-2 py-1 border border-[#FF8A85]/40 text-[#FF8A85] hover:bg-[#FF8A85]/10 disabled:opacity-40 inline-flex items-center gap-1"
            data-testid="pe-license-file-delete-btn">
            <Trash size={12} weight="bold" /> delete
          </button>
        </div>
      ) : (
        <div className="mt-3 text-xs font-mono text-gray-500"
             data-testid="pe-license-file-empty">
          No license file uploaded yet.
        </div>
      )}
    </div>
  );
}

